// Top-level BriefDocument renderer — produces HTML + Markdown in one pass.
//
// This file is the SINGLE SOURCE OF TRUTH for HTML/Markdown output.

import DOMPurify from "isomorphic-dompurify";
import type {
  BriefDocument,
  InlineNode,
  RenderContext,
  RenderedBriefOutput,
  ResolvedFootnote,
} from "../types";
import {
  renderInline,
  renderListItems,
  renderParagraph,
} from "./inline";
import {
  renderBibliographyBlock,
  renderFootnotesBlock,
  renderSignatureImage,
} from "./blocks";
import { renderFootnoteBodyHtml, renderFootnoteBodyMd } from "../footnoteBody";

/**
 * Defense-in-depth: scrub the assembled HTML through DOMPurify before any
 * consumer hands it to `dangerouslySetInnerHTML`. The renderer already
 * scheme-checks URLs (see `safeUrl` in ./inline.ts); this is belt-and-braces
 * against any future renderer regression or stored-XSS payload smuggled into a
 * BriefDocument JSON blob.
 *
 * The allow-list mirrors exactly what the block + inline renderers emit:
 *   tags  — p, h2-h4, strong, em, a, sup, br, hr, ul, ol, li, blockquote,
 *           aside, section, div, img
 *   attrs — href, target, rel, id, class, src, alt, style (signature image
 *           inline layout only), start (on <ol>), data-* hooks the consumer
 *           CSS / behavior already relies on
 */
export const sanitizeBriefHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "h2", "h3", "h4", "strong", "em", "a", "sup", "br", "hr",
      "ul", "ol", "li", "blockquote", "aside", "section", "div", "img",
    ],
    ALLOWED_ATTR: [
      "href", "target", "rel", "id", "class", "src", "alt", "style", "start",
      "data-signature-image",
    ],
    ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:|#|\/)/i,
    // `start` is treated by DOMPurify as URI-ish; mark it safe so numeric values survive the URI regex.
    ADD_URI_SAFE_ATTR: ["start"],
  });

/**
 * Render a BriefDocument to HTML + Markdown in one pass.
 *
 * Numbering: footnoteRef + endnoteRef share a single counter (document order).
 *
 * Placement:
 *   - paginated = true   →  footnoteRef body appears inline (page-bottom in PDF/DOCX),
 *                           endnoteRef body collects in trailing block.
 *   - paginated = false  →  BOTH footnoteRef and endnoteRef bodies collect in
 *                           the trailing block (web / newsletter has no pages).
 *
 * If `paginated` is unset, falls back to `ctx.mode === "footnotes"` for
 * back-compat with callers that haven't been updated.
 */
export const renderBriefDocument = (
  doc: BriefDocument,
  ctx: RenderContext,
): RenderedBriefOutput => {
  const paginated = ctx.paginated ?? ctx.mode === "footnotes";
  const warnings: string[] = [];
  const footnotes: ResolvedFootnote[] = [];
  const htmlChunks: string[] = [];
  const mdChunks: string[] = [];

  // Pre-walk: assign footnote numbers in document order. This drives both the
  // inline `<sup>` lookup and the trailing notes block so the html + markdown
  // render passes stay in lockstep (rendering is a pure lookup; numbering is
  // not re-derived per pass).
  const numberById = new Map<string, number>();
  const walkInline = (nodes: InlineNode[] | undefined) => {
    for (const n of nodes ?? []) {
      if ((n.type === "excerptus.footnoteRef" || n.type === "excerptus.endnoteRef") && !numberById.has(n.attrs.id)) {
        const number = footnotes.length + 1;
        numberById.set(n.attrs.id, number);
        footnotes.push({
          number,
          id: n.attrs.id,
          body: n.attrs.body,
          kind: n.type === "excerptus.footnoteRef" ? "footnote" : "endnote",
        });
      }
    }
  };
  for (const node of doc.content ?? []) {
    if (node.type === "paragraph" || node.type === "heading") walkInline(node.content);
    else if (node.type === "blockquote") (node.content ?? []).forEach((p) => walkInline(p.content));
    else if (node.type === "bulletList" || node.type === "orderedList") {
      (node.content ?? []).forEach((li) => (li.content ?? []).forEach((p) => walkInline(p.content)));
    } else if ((node.type === "excerptus.footnoteRef" || node.type === "excerptus.endnoteRef") && !numberById.has(node.attrs.id)) {
      const number = footnotes.length + 1;
      numberById.set(node.attrs.id, number);
      footnotes.push({
        number,
        id: node.attrs.id,
        body: node.attrs.body,
        kind: node.type === "excerptus.footnoteRef" ? "footnote" : "endnote",
      });
    }
  }
  const state = { numberById };

  for (const node of doc.content ?? []) {
    if (node.type === "paragraph") {
      htmlChunks.push(renderParagraph(node, "html", state));
      mdChunks.push(renderParagraph(node, "markdown", state));
    } else if (node.type === "heading") {
      const tag = `h${node.attrs.level}`;
      htmlChunks.push(`<${tag}>${renderInline(node.content, "html", state)}</${tag}>`);
      mdChunks.push(`${"#".repeat(node.attrs.level)} ${renderInline(node.content, "markdown", state)}`);
    } else if (node.type === "blockquote") {
      const inner = (node.content ?? []).map((p) => renderParagraph(p, "html", state)).join("");
      htmlChunks.push(`<blockquote>${inner}</blockquote>`);
      const md = (node.content ?? []).map((p) => `> ${renderInline(p.content, "markdown", state)}`).join("\n");
      mdChunks.push(md);
    } else if (node.type === "bulletList") {
      htmlChunks.push(`<ul>${renderListItems(node.content, "html", () => "", state)}</ul>`);
      mdChunks.push(renderListItems(node.content, "markdown", () => "-", state));
    } else if (node.type === "orderedList") {
      const start = node.attrs?.start ?? 1;
      htmlChunks.push(`<ol${start !== 1 ? ` start="${start}"` : ""}>${renderListItems(node.content, "html", () => "", state)}</ol>`);
      mdChunks.push(renderListItems(node.content, "markdown", (i) => `${start + i}.`, state));
    } else if (node.type === "excerptus.signatureImage") {
      htmlChunks.push(renderSignatureImage(node, "html"));
      mdChunks.push(renderSignatureImage(node, "markdown"));
    } else if (node.type === "excerptus.footnoteRef" || node.type === "excerptus.endnoteRef") {
      const number = numberById.get(node.attrs.id)!;
      const kind = node.type === "excerptus.footnoteRef" ? "footnote" : "endnote";
      const supHtml = `<sup class="footnote-ref"><a id="fnref-${node.attrs.id}" href="#fn-${node.attrs.id}">[${number}]</a></sup>`;
      const supMd = `[^${number}]`;
      if (htmlChunks.length > 0 && htmlChunks[htmlChunks.length - 1].endsWith("</p>")) {
        const last = htmlChunks[htmlChunks.length - 1];
        htmlChunks[htmlChunks.length - 1] = last.replace(/<\/p>$/, `${supHtml}</p>`);
        mdChunks[mdChunks.length - 1] = `${mdChunks[mdChunks.length - 1]}${supMd}`;
      } else {
        htmlChunks.push(`<p>${supHtml}</p>`);
        mdChunks.push(supMd);
      }
      // Footnote inline body only when paginated AND this is a footnote node.
      if (paginated && kind === "footnote") {
        htmlChunks.push(`<aside class="footnote-inline" id="fn-${node.attrs.id}"><sup>[${number}]</sup> ${renderFootnoteBodyHtml(node.attrs.body)}</aside>`);
        mdChunks.push(`> [^${number}]: ${renderFootnoteBodyMd(node.attrs.body)}`);
      }
    } else if (node.type === "excerptus.bibliography") {
      const block = renderBibliographyBlock(ctx, "html");
      const blockMd = renderBibliographyBlock(ctx, "markdown");
      if (block) htmlChunks.push(block);
      if (blockMd) mdChunks.push(blockMd);
    }
  }

  // Trailing notes block: collects endnote bodies always, plus footnote bodies
  // when not paginated.
  const trailing = footnotes.filter((f) => (paginated ? f.kind === "endnote" : true));
  if (trailing.length > 0) {
    htmlChunks.push(renderFootnotesBlock(trailing, "html"));
    mdChunks.push(renderFootnotesBlock(trailing, "markdown"));
  }

  // Auto-bibliography unless explicitly suppressed or already present in document.
  const hasExplicitBibliography = (doc.content ?? []).some((n) => n.type === "excerptus.bibliography");
  if (!hasExplicitBibliography && !ctx.hideAutoBibliography) {
    const html = renderBibliographyBlock(ctx, "html");
    const md = renderBibliographyBlock(ctx, "markdown");
    if (html) htmlChunks.push(html);
    if (md) mdChunks.push(md);
  }

  return {
    html: sanitizeBriefHtml(htmlChunks.join("\n")),
    markdown: mdChunks.join("\n\n"),
    footnotes,
    warnings: Array.from(new Set(warnings)),
  };
};
